import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import WysiwygEditor from "@/components/WysiwygEditor";
import Breadcrumbs from "@/components/Breadcrumbs";
import PrefixBadge from "@/components/PrefixBadge";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";

export default function NewThread() {
  const { forumId } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [forum, setForum] = useState(null);
  const [prefix, setPrefix] = useState("__none__");
  const [prefixes, setPrefixes] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  useEffect(() => {
    api.get(`/forums/${forumId}?page=1`).then((r) => setForum(r.data.forum)).catch(() => {});
    api.get("/prefixes").then((r) => setPrefixes(r.data.prefixes || []));
  }, [forumId]);

  const submit = async () => {
    if (!title.trim()) return toast.error("عنوان درکار ہے");
    if (!content || content.replace(/<[^>]+>/g, "").trim() === "") return toast.error("متن درکار ہے");
    setSubmitting(true);
    try {
      const { data } = await api.post("/threads", {
        forum_id: forumId,
        title: title.trim(),
        content,
        prefix: prefix === "__none__" ? null : prefix,
      });
      toast.success("موضوع بن گیا");
      nav(`/t/${data.thread_id}`);
    } catch {
      toast.error("موضوع نہیں بن سکا");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl mx-auto" data-testid="new-thread-page">
      <Breadcrumbs
        items={[
          { label: t.home, to: "/" },
          { label: forum?.name || "—", to: `/f/${forumId}` },
          { label: t.newThread },
        ]}
      />
      <h1 className="urdu-naskh text-3xl font-semibold text-primary border-b-2 border-primary pb-2">{t.newThread}</h1>
      <Card className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3 items-start">
          <div className="space-y-1.5">
            <Label className="urdu-naskh text-base">قسم (اختیاری)</Label>
            <Select value={prefix} onValueChange={setPrefix}>
              <SelectTrigger data-testid="new-thread-prefix"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__"><span className="urdu-naskh text-muted-foreground">— کچھ نہیں —</span></SelectItem>
                {prefixes.map((p) => <SelectItem key={p} value={p}><PrefixBadge prefix={p} /></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="urdu-naskh text-base">{t.title}</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="اپنا عنوان یہاں لکھیے…"
              className="text-lg"
              data-testid="new-thread-title"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="urdu-naskh text-base">{t.content}</Label>
          <WysiwygEditor value={content} onChange={setContent} placeholder={t.yourPost} minHeight={220} />
        </div>
        <div className="flex justify-end gap-2">
          <Button asChild variant="outline"><Link to={`/f/${forumId}`}>{t.cancel}</Link></Button>
          <Button onClick={submit} disabled={submitting} className="bg-primary" data-testid="new-thread-submit">
            {submitting ? t.loading : t.createThread}
          </Button>
        </div>
      </Card>
    </div>
  );
}
